import { getEnvironment } from "@sunrun/sales-experience-shared";
import axios from "axios";

type NearbyCustomerLocation = {
  latitude: number;
  longitude: number;
  distToCust?: number;
};

type NearbyCustomersResponse = {
  count: number;
  formattedCount: string;
  locations: NearbyCustomerLocation[];
};

type FetchNearbyCustomersOptions = {
  isCustomer: boolean;
  proposalToken?: string | null;
  hybridToken?: string | null;
};

const MAX_NEARBY_CUSTOMERS_TO_SHOW = 200;

const getLocationDistance = (
  originLat: number,
  originLng: number,
  location: NearbyCustomerLocation
) => {
  if (location.distToCust !== undefined) {
    return location.distToCust;
  }

  const latDelta = location.latitude - originLat;
  const lngDelta = location.longitude - originLng;

  return Math.hypot(latDelta, lngDelta);
};

const getClosestLocations = (
  locations: NearbyCustomerLocation[],
  originLat: number,
  originLng: number,
  count: number
) => {
  return [...locations]
    .sort(
      (left, right) =>
        getLocationDistance(originLat, originLng, left) -
        getLocationDistance(originLat, originLng, right)
    )
    .slice(0, count);
};

const getCustomerServiceBaseUrl = (): string => {
  switch (getEnvironment()) {
    case "production":
      return "https://gateway.sunrun.com/customers";
    case "staging":
    default:
      return "https://gateway.sunrun.com/majstg-customers";
  }
};

const fetchNearbyCustomers = async (
  latitude: number,
  longitude: number,
  { isCustomer, proposalToken, hybridToken }: FetchNearbyCustomersOptions
): Promise<NearbyCustomersResponse> => {
  const baseUrl = getCustomerServiceBaseUrl();
  const path = isCustomer ? "/customer/location" : "/location";
  const requestUrl = `${baseUrl}${path}?latitude=${latitude}&longitude=${longitude}`;

  const headers: Record<string, string> = {
    Version: "v2",
  };

  if (isCustomer && proposalToken) {
    headers.Authorization = proposalToken;
  } else if (!isCustomer && hybridToken) {
    headers.Authorization = hybridToken;
  }

  const response = await axios.get<NearbyCustomersResponse>(requestUrl, {
    headers,
  });

  return {
    ...response.data,
    locations: (response.data.locations ?? []).slice(
      0,
      MAX_NEARBY_CUSTOMERS_TO_SHOW
    ),
  };
};

export type {
  FetchNearbyCustomersOptions,
  NearbyCustomerLocation,
  NearbyCustomersResponse,
};
export {
  fetchNearbyCustomers,
  getClosestLocations,
  getCustomerServiceBaseUrl,
  MAX_NEARBY_CUSTOMERS_TO_SHOW,
};
